import { Component, Inject, OnInit } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { LoadingBoxComponent } from '../../../../../../shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';
import { UiTagComponent } from '../../../../../../shared/components/ui-tag/ui-tag.component';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxCurrencyDirective } from 'ngx-currency';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { MaskService } from '@shared/services/mask.service';
import { AcaoEtapaEnum } from '@shared/enums/compras/acao-etapa.enum';
import { TipoCampoEnum } from '@shared/enums/compras/tipo-campo.enum';
import { EtapaKanbanStruct } from '../../../../../../shared/services/structs/compras/etapa-kanban.struct';
import { NivelDadosCompraItem } from '../../../../../../shared/services/structs/compras/nivel-dados-compra-item.struct';
import { GetNiveisDadosCompraResponse } from '../../../../../../shared/services/responses/compras/get-niveis-dados-compra.response';
import { Perfil } from '../../../../../../shared/services/models/usuario/perfil.model';
import { PerfilService } from '../../../../../../shared/services/apis/usuario/perfil.service';
import { EtapasKanbanService } from '../../../../../../shared/services/apis/compras/etapas-kanban.service';
import { AutomacaoService } from '../../../../../../shared/services/apis/compras/automacao.service';
import { OrigemLancamentoEnum } from '../../../../../../shared/enums/compras/origem-lancamento.enum';
import { TipoAutomacaoEnum } from '../../../../../../shared/enums/compras/tipo-automacao.enum';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { ConfirmModalComponent } from '../../../../../../shared/components/confirm-modal/confirm-modal.component';
import { TipoConferenciaEnum } from '../../../../../../shared/enums/compras/tipo-conferencia.enum';
import { TipoRegraConfrontoEnum } from '../../../../../../shared/enums/compras/tipo-regra-confronto.enum';
import { CampoDefinicaoOpcaoRequest } from '../../../../../../shared/services/requests/compras/campo-definicao-opcao.request';
import { CampoDefinicaoRequest } from '../../../../../../shared/services/requests/compras/campo-definicao.request';
import { CampoNativoEtapaRequest } from '../../../../../../shared/services/requests/compras/campo-nativo-etapa.request';
import { MovimentacaoEtapaRequest } from '../../../../../../shared/services/requests/compras/movimentacao-etapa.request';
import { RegraAprovacaoRequest } from '../../../../../../shared/services/requests/compras/regra-aprovacao.request';
import { PermissaoPerfilStruct } from '../../../../../../shared/services/structs/compras/permissao-perfil.struct';
import { GetConfiguracaoEtapaResponse } from '../../../../../../shared/services/responses/compras/get-configuracao-etapa.response';
import { GetRegrasAprovacaoResponse } from '../../../../../../shared/services/responses/compras/get-regras-aprovacao.response';

export interface EtapaKanbanModalData {
  etapa: EtapaKanbanStruct | null;
  todasEtapas: EtapaKanbanStruct[];
  onSalvar?: (dados: EtapaKanbanModalResult) => Observable<boolean>;
}

// Payload emitido ao fechar com "Salvar" — a página orquestra a persistência.
export interface EtapaKanbanModalResult {
  salvou: boolean;
  isNova: boolean;
  etapa: EtapaKanbanStruct;
  campos: CampoDefinicaoRequest[];
  camposNativos: CampoNativoEtapaRequest[];
  movimentacoes: MovimentacaoEtapaRequest[];
  permissoes: PermissaoPerfilStruct[];
  regras: RegraAprovacaoRequest[] | null; // null quando a etapa não é a de aprovação
}

// Campo nativo da compra associado à etapa: nome fixo (vem do backend),
// usuário só configura a obrigatoriedade.
interface CampoNativoEtapa {
  idCampoNativo: number;
  nome: string;
  obrigatorio: boolean;
}

interface CampoEtapa {
  idCampoDefinicao?: number | null;
  nome: string;
  tipo: TipoCampoEnum;
  obrigatorio: boolean;
  isNativo?: boolean;
  opcoes: CampoOpcaoEtapa[];
  // Para campos de tipo "link" (Vínculo de entidade): qual entidade é vinculada.
  vinculo?: string;
}

interface CampoOpcaoEtapa {
  idCampoDefinicaoOpcao?: number | null;
  descricao: string;
}

interface FaixaAprovacao {
  id: number;
  min: number;
  max: number | null;
  perfis: number[];
  notificar: boolean;
  /** Linha já persistida. Nulo em faixa nova — o backend faz upsert por este id. */
  idRegraAprovacao: number | null;
}

interface DestinoConfig {
  selecionado: boolean;
  perfis: number[];
  exigeJustificativa: boolean;
}

@Component({
  selector: 'app-etapa-kanban-modal',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    LoadingBoxComponent,
    AlertCardComponent,
    UiTagComponent,
    DragDropModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    NgxCurrencyDirective,
    EmptyStateComponent,
  ],
  templateUrl: './etapa-kanban-modal.component.html',
  styleUrls: ['./etapa-kanban-modal.component.css'],
})
export class EtapaKanbanModalComponent implements OnInit {
  public readonly cores = [
    '#1E5DB8', '#7A4FBF', '#0F7A8B', '#C26A00', '#B73232',
    '#1E8E3E', '#5E6A2E', '#2E7D6B', '#4B5563', '#D93025',
  ];

  public readonly icones = [
    'description', 'balance', 'shopping_cart', 'rule', 'receipt_long', 'inventory_2',
    'fact_check', 'flag', 'cancel', 'error', 'pending', 'task_alt',
    'request_quote', 'local_shipping', 'storefront', 'verified',
  ];

  // Tipos que o card da etapa sabe renderizar (Sim/Não ainda não tem campo no quadro).
  public readonly tiposCampo = [
    { id: TipoCampoEnum.Texto, label: 'Texto' },
    { id: TipoCampoEnum.Numero, label: 'Número' },
    { id: TipoCampoEnum.Moeda, label: 'Moeda' },
    { id: TipoCampoEnum.Data, label: 'Data' },
    { id: TipoCampoEnum.Selecao, label: 'Seleção' },
    { id: TipoCampoEnum.SelecaoMultipla, label: 'Seleção múltipla' },
    { id: TipoCampoEnum.Vinculo, label: 'Vínculo de entidade' },
  ];

  // Entidades vinculáveis para campos do tipo "link" (Vínculo de entidade).
  public readonly vinculoEntidades = [
    { value: 'pessoa', label: 'Pessoa' },
    { value: 'produto', label: 'Produto' },
  ];

  public readonly acoes = [
    { id: AcaoEtapaEnum.Visualizar, label: 'Visualizar', icon: 'visibility' },
    { id: AcaoEtapaEnum.Editar, label: 'Editar', icon: 'edit' },
    { id: AcaoEtapaEnum.Cancelar, label: 'Cancelar', icon: 'cancel' },
  ];

  // Ações exclusivas da etapa Conferência.
  private readonly acoesConferencia = [
    { id: AcaoEtapaEnum.ConferirVolumes, label: 'Conferir volumes', icon: 'local_shipping' },
    { id: AcaoEtapaEnum.ConferirItens, label: 'Conferir itens', icon: 'inventory_2' },
    { id: AcaoEtapaEnum.ConferirProblemas, label: 'Registrar problema', icon: 'assignment_late' },
    { id: AcaoEtapaEnum.AlterarSituacaoManualmente, label: 'Alterar situação', icon: 'admin_panel_settings' },
  ];

  public readonly TipoConferenciaEnum = TipoConferenciaEnum;
  public readonly TipoRegraConfrontoEnum = TipoRegraConfrontoEnum;
  public readonly percentMask = this.maskService.getPercentMask();
  public readonly integerMask = this.maskService.getIntegerMask();
  public readonly currencyMask = this.maskService.getCurrencyMask();

  public isNova: boolean;
  public chave: string;
  public isCarregando = false;
  public isSalvando = false;

  /**
   * Perfis, configuração e regras chegaram sem erro. O PUT da etapa é replace-all: salvar com a
   * tela vazia de uma carga falha apagaria campos, movimentações, permissões e alçadas.
   */
  public configCarregada = false;

  // Perfis (lookup db_usuario)
  public perfis: Perfil[] = [];

  // Estado do formulário
  public nome = '';
  public cor = this.cores[0];
  public icone = 'description';
  public descricao = '';
  public modoVolumes = TipoConferenciaEnum.Cega;
  public modoItens = TipoConferenciaEnum.Cega;
  public aprovadoresNecessarios: number | null = 1;

  // Confronto NF x Pedido — só a etapa `faturado` usa. Até o percentual de tolerância o vínculo passa;
  // acima, Avisar pede para aceitar e ajustar ou marcar divergência e Proibir bloqueia o vínculo.
  public regraProdutoNaoCadastrado = TipoRegraConfrontoEnum.Avisar;
  public regraToleranciaPreco = TipoRegraConfrontoEnum.Avisar;
  public toleranciaPreco: number | null = 5;
  public regraToleranciaQuantidade = TipoRegraConfrontoEnum.Avisar;
  public toleranciaQuantidade: number | null = 5;

  // Níveis de dados (lookup do backend) — nada de lista fixa aqui.
  public niveis: NivelDadosCompraItem[] = [];
  public nivelGarantido = 1;
  public nivelRequisito = 1;

  public campos: CampoEtapa[] = [];
  public camposNativos: CampoNativoEtapa[] = [];
  public faixas: FaixaAprovacao[] = [];
  public destinos: { etapa: EtapaKanbanStruct; config: DestinoConfig }[] = [];
  // permissoes[idPerfil][idAcao] = boolean
  public permissoes: { [idPerfil: number]: { [idAcao: number]: boolean } } = {};

  public filtroPerfil = '';

  /** Estado das automações Lançar estoque/financeiro no Finalizado. Nulo sem carga: o bloco mostra só o texto. */
  public lancamentosFinalizado: { icone: string; nome: string; ativo: boolean; idOrigemLancamento: OrigemLancamentoEnum | null }[] | null = null;
  public readonly OrigemLancamentoEnum = OrigemLancamentoEnum;

  constructor(
    public dialogRef: MatDialogRef<EtapaKanbanModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EtapaKanbanModalData,
    private perfilService: PerfilService,
    private etapasKanbanService: EtapasKanbanService,
    private automacaoService: AutomacaoService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private maskService: MaskService
  ) {
    const etapa = data.etapa;
    this.isNova = !etapa;
    this.chave = etapa?.chave ?? '';

    if (etapa) {
      this.nome = etapa.nome;
      this.cor = etapa.cor ?? '';
      this.icone = etapa.icone ?? '';
      this.descricao = etapa.descricao ?? '';
      this.modoVolumes = etapa.idTipoConferenciaVolumes === TipoConferenciaEnum.Aberta ? TipoConferenciaEnum.Aberta : TipoConferenciaEnum.Cega;
      this.modoItens = etapa.idTipoConferenciaItens === TipoConferenciaEnum.Aberta ? TipoConferenciaEnum.Aberta : TipoConferenciaEnum.Cega;
      this.nivelGarantido = etapa.idNivelGarantido ?? 1;
      this.nivelRequisito = etapa.idNivelRequisito ?? 1;
      this.regraProdutoNaoCadastrado = etapa.idRegraProdutoNaoCadastrado === TipoRegraConfrontoEnum.Proibir ? TipoRegraConfrontoEnum.Proibir : TipoRegraConfrontoEnum.Avisar;
      this.regraToleranciaPreco = etapa.idRegraToleranciaPreco === TipoRegraConfrontoEnum.Proibir ? TipoRegraConfrontoEnum.Proibir : TipoRegraConfrontoEnum.Avisar;
      this.toleranciaPreco = etapa.toleranciaPrecoPercentual ?? 5;
      this.regraToleranciaQuantidade = etapa.idRegraToleranciaQuantidade === TipoRegraConfrontoEnum.Proibir ? TipoRegraConfrontoEnum.Proibir : TipoRegraConfrontoEnum.Avisar;
      this.toleranciaQuantidade = etapa.toleranciaQuantidadePercentual ?? 5;
    }

    this.destinos = (data.todasEtapas || [])
      .filter(e => !etapa || e.idEtapaKanban !== etapa.idEtapaKanban)
      .map(e => ({ etapa: e, config: { selecionado: false, perfis: [], exigeJustificativa: false } }));

    this.faixas = this.faixasPadrao();
  }

  ngOnInit(): void {
    // Informativo e fora do forkJoin: falha aqui não pode travar o cadastro da etapa.
    if (this.isFinalizado) {
      this.automacaoService.get().pipe(catchError(() => of(null))).subscribe(res => {
        if (!res || res.isError) return;
        const automacoes = res.automacoes || [];
        this.lancamentosFinalizado = [
          { tipo: TipoAutomacaoEnum.AtualizarSaldoEstoque, icone: 'inventory_2', nome: 'Lançar estoque' },
          { tipo: TipoAutomacaoEnum.GerarContasPagar, icone: 'payments', nome: 'Lançar financeiro' },
        ].map(item => {
          const salva = automacoes.find(a => a.idTipoAutomacao === item.tipo);
          return {
            icone: item.icone,
            nome: item.nome,
            ativo: salva?.isAtivo ?? false,
            idOrigemLancamento: salva?.idOrigemLancamento ?? null,
          };
        });
      });
    }

    const perfis$ = this.perfilService.getAllLookup();
    const niveis$ = this.etapasKanbanService.getNiveisDados();

    // Etapa nova: só precisa dos perfis e dos níveis; não há configuração a carregar. Por
    // padrão todos os perfis podem tudo (default-allow) até o admin restringir.
    if (this.isNova || !this.data.etapa) {
      // A matriz de permissões depende dos perfis: o corpo só libera quando eles chegarem.
      this.isCarregando = true;
      forkJoin({ perfis: perfis$, niveis: niveis$ }).subscribe({
        next: ({ perfis, niveis }) => {
          const perfisOk = this.aplicarPerfis(perfis);
          this.aplicarNiveis(niveis);
          this.marcarTodasPermissoes();
          this.configCarregada = perfisOk;
          this.isCarregando = false;
        },
        // Sem isto, falha em qualquer um dos lookups deixava perfis e níveis vazios em silêncio:
        // `marcarTodasPermissoes` não rodava e a etapa era salva sem nenhuma permissão.
        error: () => {
          this.isCarregando = false;
          this.alertService.show('Erro', 'Não foi possível carregar perfis e níveis de dados. Feche e abra o cadastro novamente.', AlertType.error);
        },
      });
      return;
    }

    // Edição: carrega tudo de uma vez e só libera o corpo do modal quando
    // perfis + configuração (+ regras, na aprovação) chegarem — evita o "flash".
    this.isCarregando = true;
    forkJoin({
      perfis: perfis$,
      niveis: niveis$,
      config: this.etapasKanbanService.getConfiguracao(this.data.etapa.idEtapaKanban),
      regras: this.isAprovacao ? this.etapasKanbanService.getRegrasAprovacao() : of(null),
    }).subscribe({
      next: ({ perfis, niveis, config, regras }) => {
        const perfisOk = this.aplicarPerfis(perfis);
        this.aplicarNiveis(niveis);
        const configOk = this.aplicarConfiguracao(config);
        const regrasOk = this.aplicarRegras(regras);
        this.configCarregada = perfisOk && configOk && regrasOk;
        this.isCarregando = false;
      },
      error: () => {
        this.isCarregando = false;
        this.alertService.show('Erro', 'Não foi possível carregar a configuração da etapa. Feche e abra o cadastro novamente.', AlertType.error);
      },
    });
  }

  // ----------------------------------------------------------------------
  // Aplicação dos dados carregados
  // ----------------------------------------------------------------------
  private aplicarPerfis(response: { isError: boolean; errorDescription?: string; listaPerfil?: { perfil: Perfil }[] }): boolean {
    if (response.isError) {
      this.alertService.show('Erro', response.errorDescription || 'Não foi possível carregar os perfis. Feche e abra o cadastro novamente.', AlertType.error);
      return false;
    }

    this.perfis = (response.listaPerfil ?? []).map(item => item.perfil);
    return true;
  }

  private aplicarConfiguracao(response: GetConfiguracaoEtapaResponse): boolean {
    if (response.isError) {
      this.alertService.show('Erro', response.errorDescription || 'Não foi possível carregar a configuração da etapa. Feche e abra o cadastro novamente.', AlertType.error);
      return false;
    }

    this.campos = (response.campos ?? []).map(c => {
      const tipo = c.idTipoCampo as TipoCampoEnum;
      const opcoesOrdenadas = (c.opcoes ?? []).sort((a, b) => a.ordem - b.ordem);
      return {
        idCampoDefinicao: c.idCampoDefinicao,
        nome: c.nome,
        tipo,
        obrigatorio: c.isObrigatorio,
        // Campos sistêmicos do backend são nativos: somente leitura, não reordenáveis nem removíveis.
        isNativo: c.isSistemico,
        // Para "link", a entidade vinculada vem persistida como a primeira (única) opção.
        vinculo: tipo === TipoCampoEnum.Vinculo ? (opcoesOrdenadas[0]?.descricao ?? 'pessoa') : undefined,
        opcoes: opcoesOrdenadas.map(o => ({
          idCampoDefinicaoOpcao: o.idCampoDefinicaoOpcao,
          descricao: o.descricao,
        })),
      };
    });

    // Aprovação não tem campos nativos (a aprovação pode ocorrer antes da cotação,
    // quando o valor total ainda não existe). Ignora o que vier do backend por segurança.
    this.camposNativos = this.isAprovacao
      ? []
      : (response.camposNativos ?? []).map(c => ({
          idCampoNativo: c.idCampoNativo,
          nome: c.nome,
          obrigatorio: c.isObrigatorio,
        }));

    (response.movimentacoes ?? []).forEach(m => {
      const destino = this.destinos.find(d => d.etapa.idEtapaKanban === m.idEtapaKanbanDestino);
      if (destino) {
        destino.config.selecionado = true;
        destino.config.perfis = m.idsPerfil ?? [];
        destino.config.exigeJustificativa = m.isExigeJustificativa;
      }
    });

    // Movimentação legada pode violar a regra de nível atual: `montarMovimentacoes` a descartaria
    // no save enquanto a caixa aparecia marcada — o admin salvaria "com sucesso" e perderia a
    // configuração sem ver. Desmarca aqui para a tela mostrar o que será realmente gravado.
    const bloqueadasCarregadas = this.destinos.filter(d => d.config.selecionado && this.destinoBloqueado(d));
    if (bloqueadasCarregadas.length > 0) {
      this.onNivelGarantidoChange();
      this.alertService.show(
        'Atenção',
        `Movimentações existentes deixaram de ser permitidas pelo nível de dados desta etapa e foram desmarcadas: ${bloqueadasCarregadas.map(d => d.etapa.nome).join(', ')}.`,
        AlertType.warning,
      );
    }

    this.permissoes = {};
    if ((response.permissoes ?? []).length === 0) {
      // Etapa ainda não configurada: default-allow — todos os perfis podem tudo.
      this.marcarTodasPermissoes();
    } else {
      (response.permissoes ?? []).forEach(p => {
        this.permissoes[p.idPerfil] = {};
        (p.idsAcao ?? []).forEach(idAcao => {
          if (this.acoesVisiveis.some(a => a.id === idAcao)) this.permissoes[p.idPerfil][idAcao] = true;
        });
      });
    }

    return true;
  }

  // Marca todas as ações visíveis para todos os perfis. Usado como default
  // "todos podem tudo" quando a etapa ainda não possui permissões salvas; a
  // primeira gravação persiste essas linhas em etapa_kanban_perfil_acao.
  private marcarTodasPermissoes(): void {
    this.permissoes = {};
    this.perfis.forEach(p => {
      this.permissoes[p.idPerfil] = {};
      this.acoesVisiveis.forEach(a => {
        this.permissoes[p.idPerfil][a.id] = true;
      });
    });
  }

  /** Nulo quando a etapa não é a de aprovação. Sem regras gravadas, ficam as faixas padrão. */
  private aplicarRegras(response: GetRegrasAprovacaoResponse | null): boolean {
    if (!response) return true;
    if (response.isError) {
      this.alertService.show('Erro', response.errorDescription || 'Não foi possível carregar as condições de aprovação. Feche e abra o cadastro novamente.', AlertType.error);
      return false;
    }
    if (!(response.regras?.length)) return true;

    this.faixas = response.regras.map((r, i) => ({
      id: i + 1,
      min: r.valorMinimo,
      max: r.valorMaximo,
      perfis: r.idsPerfil ?? [],
      notificar: r.isNotificarAprovadores ?? false,
      idRegraAprovacao: r.idRegraAprovacao ?? null,
    }));
    this.aprovadoresNecessarios = response.regras[0].qtdAprovadoresRequeridos || 1;
    return true;
  }

  // ----------------------------------------------------------------------
  // Flags de exibição condicional
  // ----------------------------------------------------------------------
  public get isConferencia(): boolean {
    return this.chave === 'conferencia';
  }

  public get isAprovacao(): boolean {
    return this.chave === 'aprovacao';
  }

  public get isPedidoFaturado(): boolean {
    return this.chave === 'faturado';
  }

  public get isCancelado(): boolean {
    return this.chave === 'cancelado';
  }

  public get isFinalizado(): boolean {
    return this.chave === 'finalizado';
  }

  // ----------------------------------------------------------------------
  // Níveis de dados e destinos bloqueados
  // ----------------------------------------------------------------------
  private aplicarNiveis(response: GetNiveisDadosCompraResponse | null): void {
    if (!response) return;
    if (response.isError) {
      this.alertService.show('Erro', response.errorDescription || 'Não foi possível carregar os níveis de dados.', AlertType.error);
      return;
    }
    this.niveis = response.lista ?? [];
  }

  /** Etapa padrão do sistema tem níveis fixos — o backend também recusa a alteração. */
  public get isNiveisBloqueados(): boolean {
    return this.isEtapaPadrao;
  }

  public descricaoNivel(idNivel: number): string {
    return this.niveis.find(n => n.idNivelDadosCompra === idNivel)?.descricao ?? String(idNivel);
  }

  /**
   * Um destino não é configurável quando exige mais dados do que esta etapa garante na saída,
   * ou quando esta etapa é final. Mesma regra do MovimentacaoEtapaHelper no backend, derivada
   * dos níveis que vêm do banco — sem lista de chaves aqui.
   */
  public destinoBloqueado(d: { etapa: EtapaKanbanStruct }): boolean {
    if (this.data.etapa?.isFinal) return true;
    return (d.etapa.idNivelRequisito ?? 1) > this.nivelGarantido;
  }

  public motivoBloqueio(d: { etapa: EtapaKanbanStruct }): string {
    if (this.data.etapa?.isFinal) {
      return `"${this.nome || 'Esta etapa'}" é uma etapa final e não pode ter movimentações de saída.`;
    }
    return `"${d.etapa.nome}" exige "${this.descricaoNivel(d.etapa.idNivelRequisito)}" e esta etapa garante apenas "${this.descricaoNivel(this.nivelGarantido)}".`;
  }

  /**
   * Baixar o nível garantido pode invalidar destinos já marcados. Desmarca na hora, senão o
   * backend recusaria o save inteiro por causa de uma caixa que o admin não vê mais como ativa.
   */
  public onNivelGarantidoChange(): void {
    for (const d of this.destinos) {
      if (d.config.selecionado && this.destinoBloqueado(d)) {
        d.config.selecionado = false;
      }
    }
  }

  public nomePerfil(idPerfil: number): string {
    return this.perfis.find(p => p.idPerfil === idPerfil)?.nome ?? String(idPerfil);
  }

  public removerPerfilDestino(d: { config: DestinoConfig }, idPerfil: number): void {
    d.config.perfis = d.config.perfis.filter(id => id !== idPerfil);
  }

  /**
   * Etapa criada pelo sistema (não pelo usuário). É a condição-raiz de várias
   * restrições da tela: nome e níveis ficam bloqueados e a exclusão é proibida.
   */
  public get isEtapaPadrao(): boolean {
    return !!this.data.etapa?.isFixo;
  }

  /** Nome da etapa padrão é fixo — o backend também recusa a alteração. */
  public get isNomeBloqueado(): boolean {
    return this.isEtapaPadrao;
  }

  // Cancelar vale para toda etapa que ainda pode ter o card cancelado; quem decide se o cancelamento
  // acontece é o backend, pela movimentação configurada.
  public get acoesVisiveis() {
    const base = this.acoes.filter(a => a.id !== AcaoEtapaEnum.Cancelar || (!this.isFinalizado && !this.isCancelado));
    return this.isConferencia ? [...base, ...this.acoesConferencia] : base;
  }

  public get perfisFiltrados(): Perfil[] {
    const termo = this.filtroPerfil.trim().toLowerCase();
    return this.perfis.filter(p => p.nome.toLowerCase().includes(termo));
  }

  public get totalDestinos(): number {
    return this.destinos.filter(d => d.config.selecionado).length;
  }

  public get etapasPrincipaisDestino() {
    return this.destinos.filter(d => !d.etapa.isEspecial);
  }

  public get etapasEspeciaisDestino() {
    return this.destinos.filter(d => d.etapa.isEspecial);
  }

  // ----------------------------------------------------------------------
  // Identidade
  // ----------------------------------------------------------------------
  public selecionarCor(c: string): void {
    this.cor = c;
  }

  public selecionarIcone(i: string): void {
    this.icone = i;
  }

  // ----------------------------------------------------------------------
  // Campos
  // ----------------------------------------------------------------------
  public adicionarCampo(): void {
    this.campos.push({ nome: '', tipo: TipoCampoEnum.Texto, obrigatorio: false, isNativo: false, opcoes: [] });
  }

  public removerCampo(index: number): void {
    if (this.campos[index]?.isNativo) return;
    this.campos.splice(index, 1);
  }

  public reordenarCampos(event: CdkDragDrop<CampoEtapa[]>): void {
    if (this.campos[event.previousIndex]?.isNativo) return;
    moveItemInArray(this.campos, event.previousIndex, event.currentIndex);
  }

  public isCampoComOpcoes(campo: CampoEtapa): boolean {
    return campo.tipo === TipoCampoEnum.Selecao || campo.tipo === TipoCampoEnum.SelecaoMultipla;
  }

  public isCampoVinculo(campo: CampoEtapa): boolean {
    return campo.tipo === TipoCampoEnum.Vinculo;
  }

  public adicionarOpcao(campo: CampoEtapa): void {
    if (campo.isNativo || !this.isCampoComOpcoes(campo)) return;
    campo.opcoes.push({ descricao: '' });
  }

  public removerOpcao(campo: CampoEtapa, index: number): void {
    if (campo.isNativo) return;
    campo.opcoes.splice(index, 1);
  }

  public onTipoCampoChange(campo: CampoEtapa): void {
    if (this.isCampoVinculo(campo)) {
      campo.opcoes = [];
      if (!campo.vinculo) campo.vinculo = 'pessoa';
      return;
    }

    campo.vinculo = undefined;

    if (!this.isCampoComOpcoes(campo)) {
      campo.opcoes = [];
      return;
    }

    if (!campo.opcoes.length) {
      campo.opcoes = [{ descricao: '' }];
    }
  }

  // ----------------------------------------------------------------------
  // Faixas de aprovação
  // ----------------------------------------------------------------------
  public adicionarFaixa(): void {
    const ordenadas = [...this.faixas].sort((a, b) => a.min - b.min);
    const ultimoMin = ordenadas.length ? ordenadas[ordenadas.length - 1].min : 0;
    const proximoMin = ultimoMin + 5000;
    const proximoId = Math.max(0, ...this.faixas.map(f => f.id)) + 1;
    this.faixas.forEach(f => {
      if (f.max === null) f.max = proximoMin;
    });
    this.faixas.push({ id: proximoId, min: proximoMin, max: null, perfis: [], notificar: false, idRegraAprovacao: null });
  }

  /** Faixa sem perfis é aprovação automática: não há quem avisar. */
  public notificacaoDisponivel(faixa: FaixaAprovacao): boolean {
    return (faixa.perfis?.length ?? 0) > 0;
  }

  public removerFaixa(id: number): void {
    if (this.faixas.length <= 1) return;
    this.faixas = this.faixas.filter(f => f.id !== id);

    // Se a faixa removida era a aberta (sem máximo), reabre a de maior mínimo — senão
    // qualquer compra com valor acima do maior máximo configurado ficaria sem faixa
    // correspondente e, por padrão, sem exigir aprovação nenhuma.
    if (this.faixas.length && !this.faixas.some(f => f.max === null)) {
      const maiorMin = this.faixas.reduce((a, b) => (b.min > a.min ? b : a));
      maiorMin.max = null;
    }
  }

  public resumoFaixa(faixa: FaixaAprovacao, index: number): string {
    const fmt = (v: number | null) =>
      v == null ? '' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
    if (faixa.max === null) return `Acima de ${fmt(faixa.min)}`;
    if (index === 0 && faixa.min === 0) return `Até ${fmt(faixa.max)}`;
    return `De ${fmt(faixa.min)} até ${fmt(faixa.max)}`;
  }

  // ----------------------------------------------------------------------
  // Permissões (matriz) — chaveada por idPerfil
  // ----------------------------------------------------------------------
  public togglePermissao(idPerfil: number, acao: AcaoEtapaEnum): void {
    this.permissoes[idPerfil] = this.permissoes[idPerfil] || {};
    const novoValor = !this.permissoes[idPerfil][acao];
    this.permissoes[idPerfil][acao] = novoValor;

    if (acao === AcaoEtapaEnum.Editar && novoValor) {
      this.permissoes[idPerfil][AcaoEtapaEnum.Visualizar] = true;
    } else if (acao === AcaoEtapaEnum.Visualizar && !novoValor) {
      this.permissoes[idPerfil][AcaoEtapaEnum.Editar] = false;
    }
  }

  public estadoColuna(acao: AcaoEtapaEnum): 'all' | 'some' | 'none' {
    const perfis = this.perfisFiltrados;
    if (perfis.length === 0) return 'none';
    const marcados = perfis.filter(p => this.permissoes[p.idPerfil]?.[acao]).length;
    if (marcados === 0) return 'none';
    return marcados === perfis.length ? 'all' : 'some';
  }

  public toggleColuna(acao: AcaoEtapaEnum): void {
    const marcar = this.estadoColuna(acao) !== 'all';
    this.perfisFiltrados.forEach(p => {
      this.permissoes[p.idPerfil] = this.permissoes[p.idPerfil] || {};
      this.permissoes[p.idPerfil][acao] = marcar;

      if (acao === AcaoEtapaEnum.Editar && marcar) {
        this.permissoes[p.idPerfil][AcaoEtapaEnum.Visualizar] = true;
      } else if (acao === AcaoEtapaEnum.Visualizar && !marcar) {
        this.permissoes[p.idPerfil][AcaoEtapaEnum.Editar] = false;
      }
    });
  }

  // ----------------------------------------------------------------------
  // Rodapé
  // ----------------------------------------------------------------------
  public salvar(): void {
    if (!this.configCarregada) {
      this.alertService.show('Atenção', 'A configuração da etapa não foi carregada. Feche e abra o cadastro novamente.', AlertType.warning);
      return;
    }

    const erro = this.validar();
    if (erro) {
      // Erro de validação em modal de alerta (um único botão), para dar ênfase.
      this.dialog.open(ConfirmModalComponent, {
        data: {
          title: 'Atenção',
          description: erro,
          buttonConfirm: 'Entendi',
          // sem buttonClose → modal de alerta com um único botão
        },
      });
      return;
    }

    const etapaResultado: EtapaKanbanStruct = {
      idEtapaKanban: this.data.etapa?.idEtapaKanban ?? 0,
      chave: this.chave || this.gerarChave(this.nome),
      nome: this.isNomeBloqueado ? this.data.etapa!.nome : this.nome.trim(),
      descricao: this.descricao.trim(),
      cor: this.cor,
      icone: this.icone,
      ordem: this.data.etapa?.ordem ?? 0,
      isOpcional: this.data.etapa?.isOpcional ?? true,
      isAtivo: this.data.etapa?.isAtivo ?? true,
      isEspecial: this.data.etapa?.isEspecial ?? false,
      isFinal: this.data.etapa?.isFinal ?? false,
      isFixo: this.data.etapa?.isFixo ?? false,
      idTipoConferenciaVolumes: this.isConferencia ? this.modoVolumes : null,
      idTipoConferenciaItens: this.isConferencia ? this.modoItens : null,
      idNivelGarantido: this.nivelGarantido,
      idNivelRequisito: this.nivelRequisito,
      idRegraProdutoNaoCadastrado: this.isPedidoFaturado ? this.regraProdutoNaoCadastrado : null,
      idRegraToleranciaPreco: this.isPedidoFaturado ? this.regraToleranciaPreco : null,
      toleranciaPrecoPercentual: this.isPedidoFaturado ? this.toleranciaPreco : null,
      idRegraToleranciaQuantidade: this.isPedidoFaturado ? this.regraToleranciaQuantidade : null,
      toleranciaQuantidadePercentual: this.isPedidoFaturado ? this.toleranciaQuantidade : null,
      datetimeInclusao: this.data.etapa?.datetimeInclusao ?? '',
      qtdCampos: this.campos.length,
      qtdDestinos: this.totalDestinos,
      podeVisualizar: this.data.etapa?.podeVisualizar ?? true,
    };

    const result: EtapaKanbanModalResult = {
      salvou: true,
      isNova: this.isNova,
      etapa: etapaResultado,
      campos: this.montarCampos(),
      camposNativos: this.montarCamposNativos(),
      movimentacoes: this.montarMovimentacoes(),
      permissoes: this.montarPermissoes(),
      regras: this.isAprovacao ? this.montarRegras() : null,
    };
    if (!this.data.onSalvar) {
      this.dialogRef.close(result);
      return;
    }

    this.isSalvando = true;
    this.data.onSalvar(result).subscribe({
      next: salvou => {
        this.isSalvando = false;
        if (salvou) this.dialogRef.close(result);
      },
      error: () => (this.isSalvando = false),
    });
  }

  public excluir(): void {
    // Rede de segurança: etapa padrão do sistema não pode ser excluída, mesmo que
    // o botão seja acionado programaticamente (o [disabled] cobre a UI).
    if (this.isEtapaPadrao || this.isSalvando || !this.data.etapa) return;

    // Mesma confirmação da lista; a exclusão em si (e a oferta de inativar) fica com a página.
    const confirmacao = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description: `A etapa "${this.data.etapa.nome}" será removida do fluxo. Confirma a exclusão?`,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    confirmacao.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.dialogRef.close({ excluiu: true, etapa: this.data.etapa });
    });
  }

  public fechar(): void {
    if (this.isSalvando) return;
    this.dialogRef.close();
  }

  // ----------------------------------------------------------------------
  // Montagem dos payloads
  // ----------------------------------------------------------------------
  private montarCampos(): CampoDefinicaoRequest[] {
    return this.campos
      .filter(c => c.nome.trim().length > 0)
      .map((c, i) => ({
        idCampoDefinicao: c.idCampoDefinicao ?? null,
        idTipoCampo: c.tipo,
        nome: c.nome.trim(),
        isObrigatorio: c.obrigatorio,
        descricao: null,
        ordem: i,
        opcoes: this.montarOpcoes(c),
      }));
  }

  // Opções persistidas por campo: lista do dropdown para select/multiselect,
  // ou a entidade escolhida (pessoa/produto) para campos de vínculo.
  private montarOpcoes(c: CampoEtapa): CampoDefinicaoOpcaoRequest[] {
    if (this.isCampoComOpcoes(c)) {
      return c.opcoes
        .map(o => ({
          idCampoDefinicaoOpcao: o.idCampoDefinicaoOpcao ?? null,
          descricao: o.descricao.trim(),
        }))
        .filter(o => o.descricao.length > 0)
        .map((o, ordem) => ({ ...o, ordem }));
    }

    if (this.isCampoVinculo(c) && c.vinculo) {
      return [{
        idCampoDefinicaoOpcao: c.opcoes[0]?.idCampoDefinicaoOpcao ?? null,
        descricao: c.vinculo,
        ordem: 0,
      }];
    }

    return [];
  }

  private montarCamposNativos(): CampoNativoEtapaRequest[] {
    return this.camposNativos.map(c => ({
      idCampoNativo: c.idCampoNativo,
      isObrigatorio: c.obrigatorio,
    }));
  }

  private montarMovimentacoes(): MovimentacaoEtapaRequest[] {
    return this.destinos
      // Rede de segurança: o backend recusa o save inteiro se um destino bloqueado vier marcado.
      .filter(d => d.config.selecionado && !this.destinoBloqueado(d))
      .map(d => ({
        idEtapaKanbanDestino: d.etapa.idEtapaKanban,
        isExigeJustificativa: d.config.exigeJustificativa,
        idsPerfil: d.config.perfis ?? [],
      }));
  }

  private montarPermissoes(): PermissaoPerfilStruct[] {
    const acoesValidas = this.acoesVisiveis;
    return this.perfis
      .map(p => {
        const idsAcao = acoesValidas
          .filter(a => this.permissoes[p.idPerfil]?.[a.id])
          .map(a => a.id);
        return { idPerfil: p.idPerfil, idsAcao };
      })
      .filter(p => p.idsAcao.length > 0);
  }

  /** Valida o formulário antes de salvar. Retorna a mensagem de erro ou null se válido. */
  private validar(): string | null {
    if (!this.nome.trim()) {
      return 'Informe o nome da etapa.';
    }

    // Campo sem nome era descartado em silêncio por montarCampos: o save dizia "sucesso" e o
    // campo simplesmente não existia depois.
    if (this.campos.some(c => !c.nome.trim())) {
      return 'Dê um nome a todos os campos da etapa, ou remova os que estiverem em branco.';
    }

    // Toda etapa precisa de ao menos um perfil com Visualizar; sem isso os cards
    // ficariam invisíveis para todos (estado sem sentido) e, por default-allow,
    // a etapa sem nenhuma linha voltaria a liberar tudo. Para ocultar a etapa por
    // completo o caminho correto é desativá-la, não remover o Visualizar.
    const algumPerfilVisualiza = this.perfis.some(p => this.permissoes[p.idPerfil]?.[AcaoEtapaEnum.Visualizar]);
    if (this.perfis.length > 0 && !algumPerfilVisualiza) {
      return 'Nenhum perfil pode visualizar esta etapa — os cards ficariam invisíveis. Para ocultar a etapa, desative-a.';
    }

    // O banco tem CHECK de não-negativo nas duas tolerâncias; barrar aqui evita o erro cru da API.
    // `null >= 0` é verdadeiro em JS: campo vazio precisa ser barrado explicitamente.
    if (this.isPedidoFaturado
      && (this.toleranciaPreco == null || this.toleranciaPreco < 0
        || this.toleranciaQuantidade == null || this.toleranciaQuantidade < 0)) {
      return 'Informe as tolerâncias de preço e de quantidade (percentuais maiores ou iguais a zero).';
    }

    if (this.isAprovacao) {
      if (this.aprovadoresNecessarios == null || this.aprovadoresNecessarios < 1) {
        return 'Informe quantas aprovações são necessárias (no mínimo 1).';
      }
      if (this.faixas.some(f => f.min == null)) {
        return 'Informe o valor mínimo de todas as faixas de aprovação.';
      }

      const faixas = [...this.faixas].sort((a, b) => a.min - b.min);
      for (const f of faixas) {
        const min = f.min;
        if (f.max != null && min >= f.max) {
          return 'Em cada faixa de aprovação, o valor mínimo deve ser menor que o máximo.';
        }
      }
      // Faixas não podem se sobrepor: o máximo de uma deve ser <= o mínimo da próxima,
      // e somente a última faixa pode ser aberta (máximo nulo).
      for (let i = 0; i < faixas.length - 1; i++) {
        const atualMax = faixas[i].max;
        const proximoMin = faixas[i + 1].min;
        if (atualMax == null || atualMax > proximoMin) {
          return 'As faixas de aprovação não podem se sobrepor.';
        }
      }

      // A última faixa precisa ficar aberta — senão uma compra com valor acima do maior
      // máximo configurado não cai em faixa nenhuma e, por padrão, não exige aprovação.
      if (faixas.length && faixas[faixas.length - 1].max !== null) {
        return 'A última faixa de aprovação deve ficar aberta (sem valor máximo).';
      }
    }

    return null;
  }

  /** Gera uma chave válida (a-z, 0-9, _) a partir do nome, sem acentos nem caracteres especiais. */
  private gerarChave(nome: string): string {
    return nome
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private montarRegras(): RegraAprovacaoRequest[] {
    return this.faixas.map((f, i) => ({
      idRegraAprovacao: f.idRegraAprovacao,
      valorMinimo: f.min,
      valorMaximo: f.max,
      qtdAprovadoresRequeridos: this.aprovadoresNecessarios ?? 1,
      ordem: i,
      isAtivo: true,
      isNotificarAprovadores: this.notificacaoDisponivel(f) && f.notificar,
      idsPerfil: f.perfis ?? [],
    }));
  }

  // ----------------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------------
  private faixasPadrao(): FaixaAprovacao[] {
    return [
      { id: 1, min: 0, max: 500, perfis: [], notificar: false, idRegraAprovacao: null },
      { id: 2, min: 500, max: null, perfis: [], notificar: false, idRegraAprovacao: null },
    ];
  }
}
